import * as React from 'react';
import { History } from 'history';
import { createSuperAdminSchool, SuperAdminSchoolCreateData } from 'Src/helpers/service/superadmin/schools';
import { getUploadedImageUrl, uploadSuperAdminImage } from 'Src/helpers/service/image';
import { AGE_GROUPS } from 'Src/consts/school';
import { getSuperAdminPostcodes, SuperAdminPostcode } from 'Src/helpers/service/superadmin/postcodes';

const KIND_OPTIONS = [
  { value: 'School', label: 'School' },
  { value: 'SchoolUnion', label: 'School union' }
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'EMAIL_NOTIFICATIONS', label: 'Email notifications only' }
];

const SUBSCRIPTION_OPTIONS = [
  { value: 'FULL', label: 'Full' },
  { value: 'LITE', label: 'Lite' },
  { value: 'TOURNAMENTS_PARTICIPATION', label: 'Tournaments participation' },
  { value: 'FREE_COMMUNICATION', label: 'Free communication' },
  { value: 'BENTLEY_FILE_EXCHANGE', label: 'Bentley file exchange' }
];

const AGE_GROUP_NAMING_OPTIONS = [
  { value: 'ENGLISH', label: 'English' },
  { value: 'SCOTTISH', label: 'Scottish' },
  { value: 'U5-U18', label: 'U5–U18' },
  { value: 'US', label: 'US' }
];

const REGION_OPTIONS = ['GB', 'US', 'DE', 'ES', 'AE'];

const EDU_PHASE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'PRIMARY', label: 'Primary' },
  { value: 'SECONDARY', label: 'Secondary' },
  { value: 'ALL_THROUGH', label: 'All-through' }
];

const GENDER_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' }
];

const AVAILABLE_GENDER_OPTIONS = [...GENDER_OPTIONS, { value: 'MIXED', label: 'Mixed' }];

const STUDENT_VIEW_OPTIONS = [
  { value: 'SHORTEN_LASTNAME_ONLY', label: 'First name and initial' },
  { value: 'SHORTEN_FIRSTNAME_AND_LASTNAME', label: 'Initials only' }
];

const INVITE_ACTION_OPTIONS = [
  { value: 'AUTO_ACCEPT', label: 'Auto accept' },
  { value: 'MANUAL', label: 'Manual' }
];

const PERMISSION_PRESETS = [
  'ADMIN',
  'MANAGER',
  'TEACHER',
  'COACH',
  'PARENT',
  'STUDENT',
  'GOVERNOR',
  'SLT',
  'PUBLIC_BLOGGER',
  'TOURNAMENT_FOLLOWER'
];
const AVAILABLE_AGE_OPTIONS = AGE_GROUPS.ENGLISH.map((label, value) => ({ label, value }));

const PARENT_REGISTRATION_FIELD_STATUS_OPTIONS = [
  { value: 'REQUIRED', label: 'Required' },
  { value: 'OPTIONAL', label: 'Optional' },
  { value: 'HIDDEN', label: 'Hidden' }
];

const PARENT_REGISTRATION_FIELDS = [
  { key: 'childFirstName', label: 'Child first name' },
  { key: 'childLastName', label: 'Child last name' },
  { key: 'childDateOfBirth', label: 'Child date of birth' },
  { key: 'childGender', label: 'Child gender' },
  { key: 'childForm', label: 'Registration form / class' },
  { key: 'childHouse', label: 'House' }
];

interface Props {
  history: History;
}

interface SchoolDraft {
  name: string;
  pic: string;
  kind: string;
  status: string;
  subscriptionPlan: string;
  description: string;
  domain: string;
  shortCode: string;
  email: string;
  notificationEmail: string;
  sportsDepartmentEmail: string;
  phone: string;
  address: string;
  postcodeId: string;
  postcodeSearch: string;
  region: string;
  county: string;
  eduPhase: string;
  gender: string;
  availableGender: string;
  availableAges: number[];
  ageGroupsNaming: string;
  publicStudentViewType: string;
  allowedPermissionPresets: { [key: string]: boolean };
  availableForRegistration: boolean;
  studentSelfRegistrationEnabled: boolean;
  onEventInviteAction: string;
  onTournamentInviteAction: string;
  onTournamentEventInviteAction: string;
  onInviteEmailNotification: boolean;
  additionalPermissionRequestFields: { [key: string]: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED' };
}

interface State {
  draft: SchoolDraft;
  isSaving: boolean;
  isLogoUploading: boolean;
  isPostcodeLoading: boolean;
  postcodeOptions: SuperAdminPostcode[];
  errorMessage: string;
}

const getDefaultDraft = (): SchoolDraft => ({
  name: '',
  pic: '',
  kind: 'School',
  status: 'ACTIVE',
  subscriptionPlan: 'FULL',
  description: '',
  domain: '',
  shortCode: '',
  email: '',
  notificationEmail: '',
  sportsDepartmentEmail: '',
  phone: '',
  address: '',
  postcodeId: '',
  postcodeSearch: '',
  region: 'GB',
  county: '',
  eduPhase: '',
  gender: '',
  availableGender: '',
  availableAges: [],
  ageGroupsNaming: 'ENGLISH',
  publicStudentViewType: 'SHORTEN_LASTNAME_ONLY',
  allowedPermissionPresets: PERMISSION_PRESETS.reduce((result, preset) => {
    result[preset] = true;
    return result;
  }, {} as { [key: string]: boolean }),
  availableForRegistration: true,
  studentSelfRegistrationEnabled: false,
  onEventInviteAction: 'AUTO_ACCEPT',
  onTournamentInviteAction: 'MANUAL',
  onTournamentEventInviteAction: 'AUTO_ACCEPT',
  onInviteEmailNotification: false,
  additionalPermissionRequestFields: PARENT_REGISTRATION_FIELDS.reduce((result, field) => {
    result[field.key] = 'OPTIONAL';
    return result;
  }, {} as { [key: string]: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED' })
});

const isEmailValid = (value: string): boolean => value.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const addOptionalString = (
  payload: SuperAdminSchoolCreateData,
  key: keyof SuperAdminSchoolCreateData,
  value: string
) => {
  const normalizedValue = value.trim();
  if (normalizedValue !== '') {
    (payload as any)[key] = normalizedValue;
  }
};

const buildPayload = (draft: SchoolDraft): SuperAdminSchoolCreateData => {
  const payload: SuperAdminSchoolCreateData = {
    name: draft.name.trim(),
    kind: draft.kind,
    status: draft.status,
    subscriptionPlan: draft.subscriptionPlan,
    region: draft.region,
    ageGroupsNaming: draft.ageGroupsNaming,
    publicStudentViewType: draft.publicStudentViewType,
    availableAges: draft.availableAges,
    allowedPermissionPresets: draft.allowedPermissionPresets,
    availableForRegistration: draft.availableForRegistration,
    studentSelfRegistrationEnabled: draft.studentSelfRegistrationEnabled,
    triggers: {
      onEventInviteAction: draft.onEventInviteAction,
      onTournamentInviteAction: draft.onTournamentInviteAction,
      onTournamentEventInviteAction: draft.onTournamentEventInviteAction,
      onInviteEmailNotification: draft.onInviteEmailNotification
    },
    additionalPermissionRequestFields: draft.additionalPermissionRequestFields
  };

  addOptionalString(payload, 'pic', draft.pic);
  addOptionalString(payload, 'description', draft.description);
  addOptionalString(payload, 'domain', draft.domain.toLowerCase());
  addOptionalString(payload, 'shortCode', draft.shortCode);
  addOptionalString(payload, 'email', draft.email.toLowerCase());
  addOptionalString(payload, 'notificationEmail', draft.notificationEmail.toLowerCase());
  addOptionalString(payload, 'sportsDepartmentEmail', draft.sportsDepartmentEmail.toLowerCase());
  addOptionalString(payload, 'phone', draft.phone);
  addOptionalString(payload, 'address', draft.address);
  addOptionalString(payload, 'postcodeId', draft.postcodeId);
  addOptionalString(payload, 'county', draft.county);
  addOptionalString(payload, 'eduPhase', draft.eduPhase);
  addOptionalString(payload, 'gender', draft.gender);
  addOptionalString(payload, 'availableGender', draft.availableGender);

  return payload;
};

const getSaveErrorMessage = (error: any): string => {
  const responseData = error && error.response ? error.response.data : undefined;
  const apiMessage = responseData && (responseData.message || responseData.error);
  return typeof apiMessage === 'string' && apiMessage.trim() !== ''
    ? apiMessage
    : 'Can not create school. Please check the fields and try again.';
};

export class SuperAdminSchoolForm extends React.Component<Props, State> {
  state: State = {
    draft: getDefaultDraft(),
    isSaving: false,
    isLogoUploading: false,
    isPostcodeLoading: false,
    postcodeOptions: [],
    errorMessage: ''
  };

  postcodeSearchTimer?: number;
  postcodeSearchRequest = 0;

  componentWillUnmount() {
    if (this.postcodeSearchTimer) window.clearTimeout(this.postcodeSearchTimer);
  }

  onPostcodeSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const postcodeSearch = event.target.value;
    if (this.postcodeSearchTimer) window.clearTimeout(this.postcodeSearchTimer);

    this.setState(previousState => ({
      draft: { ...previousState.draft, postcodeSearch, postcodeId: '' },
      postcodeOptions: [],
      isPostcodeLoading: postcodeSearch.trim().length >= 2,
      errorMessage: ''
    }));

    if (postcodeSearch.trim().length < 2) return;
    this.postcodeSearchTimer = window.setTimeout(() => this.loadPostcodes(postcodeSearch), 250);
  };

  onRegionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const region = event.target.value;
    if (this.postcodeSearchTimer) window.clearTimeout(this.postcodeSearchTimer);
    this.postcodeSearchRequest += 1;
    this.setState(previousState => ({
      draft: { ...previousState.draft, region, postcodeId: '', postcodeSearch: '' },
      postcodeOptions: [],
      isPostcodeLoading: false,
      errorMessage: ''
    }));
  };

  loadPostcodes = (search: string) => {
    const request = ++this.postcodeSearchRequest;
    getSuperAdminPostcodes({
      limit: 12,
      skip: 0,
      order: 'postcodeNoSpaces ASC',
      where: { postcode: { like: search.trim() }, region: this.state.draft.region }
    })
      .then(postcodeOptions => {
        if (request === this.postcodeSearchRequest) {
          this.setState({ postcodeOptions, isPostcodeLoading: false });
        }
      })
      .catch(error => {
        console.error('Can not search postcodes', error);
        if (request === this.postcodeSearchRequest) {
          this.setState({
            postcodeOptions: [],
            isPostcodeLoading: false,
            errorMessage: 'Can not search postcodes. Please try again.'
          });
        }
      });
  };

  onPostcodeSelect = (postcode: SuperAdminPostcode) => {
    const postcodeId = postcode.id || (postcode as any)._id;
    const postcodeSearch = postcode.postcode || postcode.postcodeNoSpaces || '';
    this.postcodeSearchRequest += 1;
    this.setState(previousState => ({
      draft: {
        ...previousState.draft,
        postcodeId,
        postcodeSearch,
        region: postcode.region || previousState.draft.region
      },
      postcodeOptions: [],
      isPostcodeLoading: false,
      errorMessage: ''
    }));
  };

  onInputChange = (
    field: keyof SchoolDraft,
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    this.setState(previousState => ({
      draft: {
        ...previousState.draft,
        [field]: value
      },
      errorMessage: ''
    }));
  };

  onParentRegistrationFieldChange = (field: string, event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      previousState =>
        ({
          draft: {
            ...previousState.draft,
            additionalPermissionRequestFields: {
              ...previousState.draft.additionalPermissionRequestFields,
              [field]: event.target.value
            }
          },
          errorMessage: ''
        } as State)
    );
  };

  onBooleanChange = (field: keyof SchoolDraft, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    this.setState(
      previousState =>
        ({
          draft: { ...previousState.draft, [field]: value },
          errorMessage: ''
        } as State)
    );
  };

  onAvailableAgeToggle = (age: number) => {
    this.setState(previousState => {
      const current = previousState.draft.availableAges;
      const availableAges = current.includes(age)
        ? current.filter(value => value !== age)
        : [...current, age].sort((left, right) => left - right);
      return { draft: { ...previousState.draft, availableAges }, errorMessage: '' } as State;
    });
  };

  onPermissionPresetToggle = (preset: string) => {
    this.setState(
      previousState =>
        ({
          draft: {
            ...previousState.draft,
            allowedPermissionPresets: {
              ...previousState.draft.allowedPermissionPresets,
              [preset]: !previousState.draft.allowedPermissionPresets[preset]
            }
          },
          errorMessage: ''
        } as State)
    );
  };

  onLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';

    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.setState({ errorMessage: 'Please select an image file.' });
      return;
    }

    this.setState({ isLogoUploading: true, errorMessage: '' });
    uploadSuperAdminImage(file)
      .then(response => {
        const pic = getUploadedImageUrl(response);
        if (!pic) throw new Error('Image upload returned no URL.');

        this.setState(
          previousState =>
            ({
              isLogoUploading: false,
              draft: {
                ...previousState.draft,
                pic
              }
            } as State)
        );
      })
      .catch(error => {
        console.error('Can not upload school logo', error);
        this.setState({
          isLogoUploading: false,
          errorMessage: 'Can not upload the school logo. Please try another image.'
        });
      });
  };

  validate(): string {
    const { draft } = this.state;
    if (draft.name.trim() === '') return 'School name is required.';
    if (!draft.postcodeId) return 'Please select the school postcode from the postcode list.';
    if (!isEmailValid(draft.email)) return 'School email is not valid.';
    if (!isEmailValid(draft.notificationEmail)) return 'Notification email is not valid.';
    if (!isEmailValid(draft.sportsDepartmentEmail)) return 'Sports department email is not valid.';
    return '';
  }

  onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = this.validate();
    if (validationError) {
      this.setState({ errorMessage: validationError });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '' });
    createSuperAdminSchool(buildPayload(this.state.draft))
      .then(school => {
        const schoolId = school && (school.id || school._id);
        this.props.history.push(schoolId ? `/schools/${schoolId}` : '/schools');
      })
      .catch(error => {
        console.error('Can not create superadmin school', error);
        this.setState({
          isSaving: false,
          errorMessage: getSaveErrorMessage(error)
        });
      });
  };

  render() {
    const { draft, errorMessage, isLogoUploading, isPostcodeLoading, postcodeOptions, isSaving } = this.state;
    const isBusy = isSaving || isLogoUploading;

    return (
      <div className="container-fluid py-4">
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">Add school</h2>
              <div className="text-muted">
                Create the school first, then configure its modules and advanced settings.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={isBusy}
              onClick={() => this.props.history.push('/schools')}
            >
              Back to schools
            </button>
          </div>

          {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}

          <form onSubmit={this.onSubmit}>
            <div className="card mb-3">
              <div className="card-header font-weight-bold">Basic details</div>
              <div className="card-body">
                <div className="form-group">
                  <label>
                    <strong>School logo</strong>
                  </label>
                  <div className="d-flex align-items-center flex-wrap">
                    <div
                      className="border rounded bg-light d-flex align-items-center justify-content-center mr-3 mb-2"
                      style={{ width: 112, height: 112, overflow: 'hidden' }}
                    >
                      {draft.pic ? (
                        <img
                          src={draft.pic}
                          alt="School logo preview"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <span className="text-muted small">No logo</span>
                      )}
                    </div>
                    <div className="mb-2">
                      <label className={`btn btn-sm btn-outline-primary mb-2 ${isBusy ? 'disabled' : ''}`}>
                        {isLogoUploading ? 'Uploading…' : draft.pic ? 'Replace logo' : 'Upload logo'}
                        <input
                          type="file"
                          accept="image/*"
                          className="d-none"
                          disabled={isBusy}
                          onChange={this.onLogoChange}
                        />
                      </label>
                      {draft.pic ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger d-block p-0"
                          disabled={isBusy}
                          onClick={() => this.setState({ draft: { ...draft, pic: '' } })}
                        >
                          Remove logo
                        </button>
                      ) : null}
                      <div className="text-muted small mt-1">PNG, JPEG, GIF or another browser-supported image.</div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-name">Name *</label>
                      <input
                        id="superadmin-school-name"
                        type="text"
                        className="form-control"
                        value={draft.name}
                        autoFocus
                        onChange={event => this.onInputChange('name', event)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-kind">Type</label>
                      <select
                        id="superadmin-school-kind"
                        className="form-control"
                        value={draft.kind}
                        onChange={event => this.onInputChange('kind', event)}
                      >
                        {KIND_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-status">Status</label>
                      <select
                        id="superadmin-school-status"
                        className="form-control"
                        value={draft.status}
                        onChange={event => this.onInputChange('status', event)}
                      >
                        {STATUS_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-subscription">Subscription plan</label>
                      <select
                        id="superadmin-school-subscription"
                        className="form-control"
                        value={draft.subscriptionPlan}
                        onChange={event => this.onInputChange('subscriptionPlan', event)}
                      >
                        {SUBSCRIPTION_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-domain">Domain</label>
                      <input
                        id="superadmin-school-domain"
                        type="text"
                        className="form-control"
                        value={draft.domain}
                        placeholder="school.example.com"
                        onChange={event => this.onInputChange('domain', event)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-short-code">Short code</label>
                      <input
                        id="superadmin-school-short-code"
                        type="text"
                        className="form-control"
                        value={draft.shortCode}
                        onChange={event => this.onInputChange('shortCode', event)}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group mb-0">
                  <label htmlFor="superadmin-school-description">Description</label>
                  <textarea
                    id="superadmin-school-description"
                    className="form-control"
                    rows={3}
                    value={draft.description}
                    onChange={event => this.onInputChange('description', event)}
                  />
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header font-weight-bold">Contact details</div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-email">School email</label>
                      <input
                        id="superadmin-school-email"
                        type="email"
                        className="form-control"
                        value={draft.email}
                        onChange={event => this.onInputChange('email', event)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-notification-email">Notification email</label>
                      <input
                        id="superadmin-school-notification-email"
                        type="email"
                        className="form-control"
                        value={draft.notificationEmail}
                        onChange={event => this.onInputChange('notificationEmail', event)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-sports-email">Sports department email</label>
                      <input
                        id="superadmin-school-sports-email"
                        type="email"
                        className="form-control"
                        value={draft.sportsDepartmentEmail}
                        onChange={event => this.onInputChange('sportsDepartmentEmail', event)}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group mb-md-0">
                      <label htmlFor="superadmin-school-phone">Phone</label>
                      <input
                        id="superadmin-school-phone"
                        type="tel"
                        className="form-control"
                        value={draft.phone}
                        onChange={event => this.onInputChange('phone', event)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-0">
                      <label htmlFor="superadmin-school-address">Address</label>
                      <input
                        id="superadmin-school-address"
                        type="text"
                        className="form-control"
                        value={draft.address}
                        onChange={event => this.onInputChange('address', event)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group mb-0 position-relative">
                      <label htmlFor="superadmin-school-postcode">Postcode *</label>
                      <input
                        id="superadmin-school-postcode"
                        type="text"
                        className={`form-control ${draft.postcodeSearch && !draft.postcodeId ? 'is-invalid' : ''}`}
                        value={draft.postcodeSearch}
                        autoComplete="off"
                        placeholder="Start typing a postcode"
                        onChange={this.onPostcodeSearchChange}
                      />
                      {isPostcodeLoading ? <small className="text-muted">Searching…</small> : null}
                      {postcodeOptions.length > 0 ? (
                        <div
                          className="list-group position-absolute w-100 shadow"
                          style={{ zIndex: 20, maxHeight: 240, overflowY: 'auto' }}
                        >
                          {postcodeOptions.map(postcode => {
                            const id = postcode.id || (postcode as any)._id;
                            return (
                              <button
                                type="button"
                                className="list-group-item list-group-item-action py-2"
                                key={id}
                                onClick={() => this.onPostcodeSelect(postcode)}
                              >
                                <strong>{postcode.postcode || postcode.postcodeNoSpaces}</strong>
                                {postcode.region ? <small className="text-muted ml-2">{postcode.region}</small> : null}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                      <small className="form-text text-muted">
                        Select a postcode from the shared postcode directory.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header font-weight-bold">Regional settings</div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label htmlFor="superadmin-school-region">Region</label>
                      <select
                        id="superadmin-school-region"
                        className="form-control"
                        value={draft.region}
                        onChange={this.onRegionChange}
                      >
                        {REGION_OPTIONS.map(region => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-0">
                      <label htmlFor="superadmin-school-age-groups">Age group naming</label>
                      <select
                        id="superadmin-school-age-groups"
                        className="form-control"
                        value={draft.ageGroupsNaming}
                        onChange={event => this.onInputChange('ageGroupsNaming', event)}
                      >
                        {AGE_GROUP_NAMING_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-0">
                      <label htmlFor="superadmin-school-county">County / state</label>
                      <input
                        id="superadmin-school-county"
                        className="form-control"
                        value={draft.county}
                        onChange={event => this.onInputChange('county', event)}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label htmlFor="superadmin-school-edu-phase">Education phase</label>
                      <select
                        id="superadmin-school-edu-phase"
                        className="form-control"
                        value={draft.eduPhase}
                        onChange={event => this.onInputChange('eduPhase', event)}
                      >
                        {EDU_PHASE_OPTIONS.map(option => (
                          <option key={option.value || 'empty'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label htmlFor="superadmin-school-gender">School gender</label>
                      <select
                        id="superadmin-school-gender"
                        className="form-control"
                        value={draft.gender}
                        onChange={event => this.onInputChange('gender', event)}
                      >
                        {GENDER_OPTIONS.map(option => (
                          <option key={option.value || 'empty'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-0">
                      <label htmlFor="superadmin-school-available-gender">Available pupil genders</label>
                      <select
                        id="superadmin-school-available-gender"
                        className="form-control"
                        value={draft.availableGender}
                        onChange={event => this.onInputChange('availableGender', event)}
                      >
                        {AVAILABLE_GENDER_OPTIONS.map(option => (
                          <option key={option.value || 'empty'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="d-block">Available ages</label>
                  <div className="d-flex flex-wrap">
                    {AVAILABLE_AGE_OPTIONS.map(option => (
                      <label className="mr-3 mb-2" key={option.value}>
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={draft.availableAges.includes(option.value)}
                          onChange={() => this.onAvailableAgeToggle(option.value)}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header font-weight-bold">Initial access and workflow</div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-student-view">Pupil names</label>
                      <select
                        id="superadmin-school-student-view"
                        className="form-control"
                        value={draft.publicStudentViewType}
                        onChange={event => this.onInputChange('publicStudentViewType', event)}
                      >
                        {STUDENT_VIEW_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-event-invites">Event invites</label>
                      <select
                        id="superadmin-school-event-invites"
                        className="form-control"
                        value={draft.onEventInviteAction}
                        onChange={event => this.onInputChange('onEventInviteAction', event)}
                      >
                        {INVITE_ACTION_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="superadmin-school-tournament-invites">Tournament invites</label>
                      <select
                        id="superadmin-school-tournament-invites"
                        className="form-control"
                        value={draft.onTournamentInviteAction}
                        onChange={event => this.onInputChange('onTournamentInviteAction', event)}
                      >
                        {INVITE_ACTION_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-md-0">
                      <label htmlFor="superadmin-school-tournament-event-invites">Tournament event invites</label>
                      <select
                        id="superadmin-school-tournament-event-invites"
                        className="form-control"
                        value={draft.onTournamentEventInviteAction}
                        onChange={event => this.onInputChange('onTournamentEventInviteAction', event)}
                      >
                        {INVITE_ACTION_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-8 d-flex align-items-end flex-wrap">
                    <label className="mr-4 mb-2">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={draft.availableForRegistration}
                        onChange={event => this.onBooleanChange('availableForRegistration', event)}
                      />
                      Available for registration
                    </label>
                    <label className="mr-4 mb-2">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={draft.studentSelfRegistrationEnabled}
                        onChange={event => this.onBooleanChange('studentSelfRegistrationEnabled', event)}
                      />
                      Student self-registration
                    </label>
                    <label className="mb-2">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={draft.onInviteEmailNotification}
                        onChange={event => this.onBooleanChange('onInviteEmailNotification', event)}
                      />
                      Invite email notification
                    </label>
                  </div>
                </div>
                <hr />
                <label className="d-block">Allowed roles</label>
                <div className="d-flex flex-wrap">
                  {PERMISSION_PRESETS.map(preset => (
                    <label className="mr-4 mb-2" key={preset}>
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={draft.allowedPermissionPresets[preset] !== false}
                        onChange={() => this.onPermissionPresetToggle(preset)}
                      />
                      {preset
                        .replace(/_/g, ' ')
                        .toLowerCase()
                        .replace(/\b\w/g, letter => letter.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header font-weight-bold">Parent registration child fields</div>
              <div className="card-body">
                <div className="text-muted small mb-3">
                  Choose whether each detail is required, optional or hidden when a parent requests access to the
                  school.
                </div>
                <div className="row">
                  {PARENT_REGISTRATION_FIELDS.map(field => (
                    <div className="col-md-6 col-lg-4" key={field.key}>
                      <div className="form-group">
                        <label htmlFor={`new_school_parent_registration_${field.key}`}>{field.label}</label>
                        <select
                          id={`new_school_parent_registration_${field.key}`}
                          className="form-control"
                          value={draft.additionalPermissionRequestFields[field.key] || 'OPTIONAL'}
                          disabled={isBusy}
                          onChange={event => this.onParentRegistrationFieldChange(field.key, event)}
                        >
                          {PARENT_REGISTRATION_FIELD_STATUS_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary mr-2"
                disabled={isBusy}
                onClick={() => this.props.history.push('/schools')}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isBusy}>
                {isLogoUploading ? 'Uploading logo…' : isSaving ? 'Creating…' : 'Create school'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}
